import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RangeStatusClient, RangeStatusDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getApiClient = () => new RangeStatusClient(API_BASE_URL, axiosInstance);

export const useRangeStatuses = () =>
  useQuery({
    queryKey: ['rangeStatuses'],
    queryFn: async () => {
      const response = await getApiClient().searchRangeStatuses({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

export const useRangeStatusSearch = () => {
  const search = async (params: any) => {
    const response = await getApiClient().searchRangeStatuses(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

export const useRangeStatusById = (id: string | undefined) =>
  useQuery({
    queryKey: ['rangeStatus', id],
    queryFn: async () => {
      const response = await getApiClient().getRangeStatusById(id!);
      return response?.data || null;
    },
    enabled: !!id,
  });

export const useCreateRangeStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RangeStatusDTO) => getApiClient().createRangeStatus(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rangeStatuses'] }),
  });
};

export const useUpdateRangeStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RangeStatusDTO }) =>
      getApiClient().updateRangeStatus(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rangeStatuses'] }),
  });
};

export const useDeleteRangeStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getApiClient().deleteRangeStatus(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rangeStatuses'] }),
  });
};